import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { Typography, Box, Grid, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../theme/colors";
import { NoDataCard, Legends } from "../../../components/parts";
import { QuantityChart } from "../../demo/components/QuantityChart";
import { squareKey, bars } from "../../demo/noSolar/steps/ReviewCalculationStep";
import {
  getData,
  onlyUniqueValuesOfKeyInArray,
  isObjectEmpty,
  sumFromListOfObjects,
} from "../../../services";
import { SolarPotentialPart } from "./SolarPotentialPart";
import { ElectricityConsumptionPart } from "./ElectricityConsumptionPart";
import { TotalRoofAreaPart } from "./TotalRoofAreaPart";
import { TotalBuildingAreaPart } from "./TotalBuildingAreaPart";

const useStyles = makeStyles(() => ({
  divider: {
    height: 15,
    width: 2,
    marginTop: 5,
  },
  chartBox: {
    marginTop: 20,
  },
  title: {
    padding: "10px 0 0 20px",
  },
}));

export const SegmentTab = ({ resetSteps }) => {
  const classes = useStyles();
  const { clusters } = useSelector((state) => state.auth);
  const preliminaryStage = clusters[0].preliminary_stage;
  const [electricityCostsChartData, setElectricityCostsChartData] = useState([]);
  const [electricityConsumptionChartData, setElectricityConsumptionChartData] = useState([]);
  const [totalAreaChartData, setTotalAreaChartData] = useState([]);
  const [roofAreaChartData, setRoofAreaChartData] = useState([]);
  const bubbleChartContainerRef = useRef();
  const [quantityChartData, setQuantityChartData] = useState([]);
  const [results, setResults] = useState([]);

  useEffect(() => {
    getData(`onboarding/base/analytics-by-segments/?clusters=${clusters[0].id}`, (data) => {
      setResults(data);
      const electricity_costs_2021 = {};
      const electricity_costs_2022 = {};
      const electricity_costs_2023 = {};

      const electricity_consumption_2021 = {};
      const electricity_consumption_2022 = {};
      const electricity_consumption_2023 = {};

      const totalAreaChartData = [];
      const roofAreaChartData = [];

      const totalCount = sumFromListOfObjects(data, "count");
      data.forEach((item) => {
        const name = item.segment.toLowerCase().replace("-", "_");
        if (item.electricity_costs._2021 !== null)
          electricity_costs_2021[name] = item.electricity_costs._2021;
        if (item.electricity_costs._2022 !== null)
          electricity_costs_2022[name] = item.electricity_costs._2022;
        if (item.electricity_costs._2023 !== null)
          electricity_costs_2023[name] = item.electricity_costs._2023;

        if (item.electricity_consumption._2021 !== null)
          electricity_consumption_2021[name] = item.electricity_consumption._2021;
        if (item.electricity_consumption._2022 !== null)
          electricity_consumption_2022[name] = item.electricity_consumption._2022;
        if (item.electricity_consumption._2023 !== null)
          electricity_consumption_2023[name] = item.electricity_consumption._2023;

        totalAreaChartData.push({
          name: item.segment,
          value: item.sum_total_square,
          color: squareKey[name],
          unit: "unit_system",
        });

        roofAreaChartData.push({
          name: item.segment,
          value: item.sum_roof_square,
          color: squareKey[name],
          unit: "unit_system",
        });

        item["percent"] = (item.count * 100) / totalCount;
      });

      setQuantityChartData(data);

      if (!onlyUniqueValuesOfKeyInArray(totalAreaChartData, "value", null)) {
        setTotalAreaChartData(totalAreaChartData);
      }
      if (!onlyUniqueValuesOfKeyInArray(roofAreaChartData, "value", null)) {
        setRoofAreaChartData(roofAreaChartData);
      }

      const electricityCosts = [];

      if (!isObjectEmpty(electricity_costs_2021)) {
        electricity_costs_2021["x"] = "2021";
        electricityCosts.push(electricity_costs_2021);
      }

      if (!isObjectEmpty(electricity_costs_2022)) {
        electricity_costs_2022["x"] = "2022";
        electricityCosts.push(electricity_costs_2022);
      }

      if (!isObjectEmpty(electricity_costs_2023)) {
        electricity_costs_2023["x"] = "2023";
        electricityCosts.push(electricity_costs_2023);
      }

      if (electricityCosts.length) {
        setElectricityCostsChartData([
          electricity_costs_2021,
          electricity_costs_2022,
          electricity_costs_2023,
        ]);
      }

      const electricityConsumption = [];
      if (!isObjectEmpty(electricity_consumption_2021)) {
        electricity_consumption_2021["x"] = "2021";
        electricity_consumption_2021["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2021);
      }

      if (!isObjectEmpty(electricity_consumption_2022)) {
        electricity_consumption_2022["x"] = "2022";
        electricity_consumption_2022["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2022);
      }

      if (!isObjectEmpty(electricity_consumption_2023)) {
        electricity_consumption_2023["x"] = "2023";
        electricity_consumption_2023["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2023);
      }

      if (electricityConsumption.length) {
        setElectricityConsumptionChartData([
          electricity_consumption_2021,
          electricity_consumption_2022,
          electricity_consumption_2023,
        ]);
      }
    }).catch((error) => {
      if (error?.response.status === 404) resetSteps();
    });

    return () => {
      setElectricityCostsChartData([]);
      setElectricityConsumptionChartData([]);
      setTotalAreaChartData([]);
      setRoofAreaChartData([]);
      setQuantityChartData([]);
    };
  }, []);

  return (
    <Box>
      <Legends />
      <Grid container spacing={2} style={{ marginBottom: 24, marginTop: 14 }}>
        <Grid item xs={4}>
          <Box style={{ backgroundColor: COLORS.darks[0][3], borderRadius: 8, height: "100%" }}>
            <Typography variant="h4" className={classes.title}>
              Quantity
            </Typography>
            <Box
              ref={bubbleChartContainerRef}
              display="flex"
              justifyContent="center"
              alignItems="center"
              className={classes.chartBox}
            >
              {quantityChartData.length ? (
                <QuantityChart data={quantityChartData} containerRef={bubbleChartContainerRef} />
              ) : (
                <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <TotalBuildingAreaPart totalAreaChartData={totalAreaChartData} />
        </Grid>
        <Grid item xs={4}>
          <TotalRoofAreaPart roofAreaChartData={roofAreaChartData} />
        </Grid>
      </Grid>
      {preliminaryStage?.owner === "TENANTS_CONSUMPTION" &&
      !electricityCostsChartData.length &&
      !electricityConsumptionChartData.length ? null : (
        <ElectricityConsumptionPart
          electricityCostsChartData={electricityCostsChartData}
          electricityConsumptionChartData={electricityConsumptionChartData}
          bars={bars}
        />
      )}
      {results.length ? (
        <SolarPotentialPart groupKey="segment" data={results} colors={squareKey} />
      ) : null}
    </Box>
  );
};
